import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '@/shared/hooks/useLanguage';

const PREVIEW_IMAGES = [
  { src: '/img/preview/prew.webp', altFa: 'پیش‌نمایش ۱', altEn: 'Preview 1' },
  { src: '/img/preview/prew2.webp', altFa: 'پیش‌نمایش ۲', altEn: 'Preview 2' },
  { src: '/img/preview/prew3.webp', altFa: 'پیش‌نمایش ۳', altEn: 'Preview 3' },
  { src: '/img/preview/prew4.webp', altFa: 'پیش‌نمایش ۴', altEn: 'Preview 4' },
  { src: '/img/preview/prew5.webp', altFa: 'پیش‌نمایش ۵', altEn: 'Preview 5' },
] as const;

const PreviewComingSoon = () => {
  const { language, isRTL } = useLanguage();
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const imageCount = PREVIEW_IMAGES.length;

  const closeLightbox = useCallback(() => setActiveIndex(null), []);

  const goToPrevious = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null) return null;
      return (current - 1 + imageCount) % imageCount;
    });
  }, [imageCount]);

  const goToNext = useCallback(() => {
    setActiveIndex((current) => {
      if (current === null) return null;
      return (current + 1) % imageCount;
    });
  }, [imageCount]);

  useEffect(() => {
    if (activeIndex === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeIndex, closeLightbox, goToNext, goToPrevious]);

  const activeImage = activeIndex !== null ? PREVIEW_IMAGES[activeIndex] : null;

  return (
    <section
      className={`w-full pt-8 pb-4 ${isRTL ? 'rtl' : 'ltr'}`}
      aria-label={language === 'fa' ? 'پیش‌نمایش به‌زودی' : 'Coming soon preview'}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 md:mb-10">
          <span className="inline-block rounded-full bg-white/15 px-4 py-1.5 text-sm font-medium text-white/90 mb-4">
            {language === 'fa' ? 'به‌زودی' : 'Coming soon'}
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-white">
            {language === 'fa'
              ? 'تا انتهای سال ۱۴۰۵'
              : 'By the end of 1405 (Persian calendar)'}
          </h2>
          <p className="mt-3 text-white/75 text-sm md:text-base max-w-2xl mx-auto">
            {language === 'fa'
              ? 'برای بزرگ‌نمایی روی هر تصویر کلیک کنید.'
              : 'Click any image to view it larger.'}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 max-w-6xl mx-auto">
          {PREVIEW_IMAGES.map((image, index) => (
            <button
              key={image.src}
              type="button"
              onClick={() => setActiveIndex(index)}
              className="group relative overflow-hidden rounded-xl border border-white/20 bg-white/10 shadow-lg transition-transform hover:scale-[1.02] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
              aria-label={
                language === 'fa'
                  ? `بزرگ‌نمایی ${image.altFa}`
                  : `Enlarge ${image.altEn}`
              }
            >
              <img
                src={image.src}
                alt={language === 'fa' ? image.altFa : image.altEn}
                className="w-full aspect-[4/3] object-cover object-top"
                loading="lazy"
              />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
            </button>
          ))}
        </div>
      </div>

      {activeImage &&
        activeIndex !== null &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center p-4 md:p-8 modal-overlay"
            style={{
              zIndex: 99999999,
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              backdropFilter: 'blur(4px)',
            }}
            onClick={closeLightbox}
            role="dialog"
            aria-modal="true"
            aria-label={language === 'fa' ? 'نمایش بزرگ تصویر' : 'Image preview'}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                closeLightbox();
              }}
              className="absolute top-4 right-4 md:top-6 md:right-6 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors z-10"
              aria-label={language === 'fa' ? 'بستن' : 'Close'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors z-10"
              aria-label={language === 'fa' ? 'تصویر قبلی' : 'Previous image'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-3 text-white hover:bg-white/20 transition-colors z-10"
              aria-label={language === 'fa' ? 'تصویر بعدی' : 'Next image'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <p className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 text-white/80 text-sm z-10">
              {(activeIndex + 1).toLocaleString(language === 'fa' ? 'fa-IR' : 'en-US')}
              {' / '}
              {imageCount.toLocaleString(language === 'fa' ? 'fa-IR' : 'en-US')}
            </p>

            <img
              key={activeImage.src}
              src={activeImage.src}
              alt={language === 'fa' ? activeImage.altFa : activeImage.altEn}
              className="max-w-full max-h-[90vh] rounded-lg shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </section>
  );
};

export default PreviewComingSoon;
